import { useEffect, useCallback, useMemo } from 'react';
import { usePWAInstallStore, PWA_DECLINE_COOLDOWN_MS } from '@/shared/stores/usePWAInstallStore';

/**
 * Detect if the browser is Safari (iOS or macOS)
 */
function isSafari(): boolean {
  const ua = navigator.userAgent;
  const isAppleDevice = /iPad|iPhone|iPod|Macintosh/.test(ua);
  const isSafariBrowser = ua.includes('Safari') && !/Chrome|CriOS|FxiOS|EdgiOS/.test(ua);
  return isAppleDevice && isSafariBrowser;
}

/**
 * Hook for managing PWA installation prompt lifecycle
 *
 * Listens for the beforeinstallprompt event and tracks installation state.
 * Provides an install function to trigger the browser's install prompt.
 * For Safari, shows instructions since it doesn't support beforeinstallprompt.
 *
 * @example
 * ```tsx
 * function InstallButton() {
 *   const { isInstallable, install } = usePWAInstall();
 *   if (!isInstallable) return null;
 *   return <button onClick={install}>Install App</button>;
 * }
 * ```
 */
export function usePWAInstall() {
  const {
    installPromptEvent,
    isInstalled,
    setInstallPromptEvent,
    setIsInstalled,
    markDeclined,
    shouldShowPrompt,
    declinedAt,
    dialogOpen,
    setDialogOpen
  } = usePWAInstallStore();

  const isSafariBrowser = useMemo(() => isSafari(), []);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as Navigator & { standalone?: boolean }).standalone === true;

    if (isStandalone) {
      setIsInstalled(true);
      return;
    }

    const handleBeforeInstallPrompt = (e: BeforeInstallPromptEvent) => {
      e.preventDefault();
      setInstallPromptEvent(e);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setInstallPromptEvent(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [setInstallPromptEvent, setIsInstalled]);

  const install = useCallback(async () => {
    if (!installPromptEvent) return false;

    try {
      await installPromptEvent.prompt();
      const { outcome } = await installPromptEvent.userChoice;

      if (outcome === 'accepted') {
        setIsInstalled(true);
      }

      setInstallPromptEvent(null);
      return outcome === 'accepted';
    } catch {
      return false;
    }
  }, [installPromptEvent, setInstallPromptEvent, setIsInstalled]);

  const decline = useCallback(() => {
    markDeclined();
  }, [markDeclined]);

  // For Safari, check if we should show based on declined cooldown
  const shouldShowSafariPrompt = useMemo(() => {
    if (!isSafariBrowser || isInstalled) return false;

    if (declinedAt !== null) {
      const timeSinceDeclined = Date.now() - declinedAt;
      if (timeSinceDeclined < PWA_DECLINE_COOLDOWN_MS) return false;
    }

    return true;
  }, [isSafariBrowser, isInstalled, declinedAt]);

  return {
    isInstalled,
    isSafari: isSafariBrowser,
    isInstallable: !!installPromptEvent || isSafariBrowser,
    shouldShowPrompt: shouldShowPrompt() || shouldShowSafariPrompt,
    dialogOpen,
    openDialog: () => {
      setDialogOpen(true);
    },
    closeDialog: () => {
      setDialogOpen(false);
    },
    install,
    decline
  };
}
